import React, { useState, useEffect } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useDispatch } from "react-redux";
import { Label } from "@progress/kendo-react-labels";
import { TextArea } from "@progress/kendo-react-inputs";
import styles from "pages/CaseFile/Casefile.module.scss";
import activityStyles from "./CaseActivity.module.scss";
import infoIcon from "assets/CaseFileIcons/info-icon.png";
import { Button } from "@progress/kendo-react-buttons";
import {
  getDocument,
  saveActivity,
  updateMatterActivity,
  getMatterRates,
  getNewBlankDocument,
} from "api/index";
import { FormComboBox, FormDate } from "components/Matters/index";
import { formatDateForAPI } from "components/common/formatter";
import { CONSTANT } from "CONSTANT/Matter/MatterActivityForm";
import MatterModal from "components/Matters/MatterModal";
import { filterBy } from "@progress/kendo-data-query";
import { useUploadForm } from "components/fileUploadHook";
import { onFileUpload } from "services/fileDataService";
import { PDFViewer } from "@progress/kendo-react-pdf-viewer";
import { Window } from "@progress/kendo-react-dialogs";
import { FileDocumentTypes } from "components/Enums/FileDocumentTypes.ts";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Tile } from "components/UIComponents/Tile";
import { FileDragDrop } from "./FileDragDrop";
import DocumentViewer from "components/common/DocumentViewer";
import { RiUpload2Fill } from "react-icons/ri";
import { toast } from "react-toastify";
import { useActiveFeeEarner, useUsers } from "services/userService";
import { RxCross2 } from "react-icons/rx";
import { useActiveActivityTypes } from "services/activityTypeService";
import {
  addActivityTagAPI,
  useMatterActivityTags,
} from "services/activityTagService";
import { PrimaryBadgeComponent } from "components/UIComponents/Badges";
import { NumericTextBox, Checkbox } from "@progress/kendo-react-inputs";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { fileIcon } from "components/common/fileIcon";

let entryKeyCounter = 0;
const nextEntryKey = () => `fe-${Date.now()}-${++entryKeyCounter}`;

const getTagId = (tag) => tag?.activityTagID ?? tag?.tagId ?? tag?.id;

const getTagMatterId = (tag) => tag?.matterId ?? tag?.matterID ?? null;

const findExistingTagByName = (tags, name, matterId) => {
  const key = name.trim().toLowerCase();
  const matterMatch = (tags || []).find(
    (item) =>
      item.name?.trim().toLowerCase() === key &&
      getTagMatterId(item) === matterId,
  );
  if (matterMatch) return matterMatch;

  return (tags || []).find(
    (item) =>
      item.name?.trim().toLowerCase() === key && getTagMatterId(item) == null,
  );
};

const NEW_TAG_PREFIX = "__new__";

const isNewTag = (tag) => {
  if (!tag) return false;
  if (tag.isNew) return true;
  const id = getTagId(tag);
  if (id == null) return true;
  return String(id).startsWith(NEW_TAG_PREFIX);
};

const createPendingTag = (name) => {
  const trimmed = String(name || "").trim();
  const key = trimmed.toLowerCase();
  return {
    name: trimmed,
    tagId: `${NEW_TAG_PREFIX}${key}`,
    activityTagID: `${NEW_TAG_PREFIX}${key}`,
    isNew: true,
  };
};

const normalizeTag = (tag) => {
  if (!tag) return tag;
  const id = getTagId(tag);
  if (id == null) return tag;
  return {
    ...tag,
    activityTagID: id,
    tagId: id,
  };
};

const mapActivityTagsToSelection = (activityTags, allTags) => {
  if (!activityTags?.length) return [];
  return activityTags.map((tag) => {
    const id = getTagId(tag);
    const matched = allTags.find((item) => getTagId(item) === id);
    return normalizeTag(
      matched || {
        activityTagID: id,
        tagId: id,
        name: tag.name,
        colour: tag.colour,
      },
    );
  });
};

const buildTagsPayload = (selectedTags, originalTags) => {
  const selectedIds = new Set((selectedTags || []).map(getTagId).filter(Boolean));
  const payload = [...selectedIds].map((tagId) => ({ tagId, active: true }));

  (originalTags || []).forEach((tag) => {
    const id = getTagId(tag);
    if (id != null && !selectedIds.has(id)) {
      payload.push({ tagId: id, active: false });
    }
  });

  return payload;
};

const hasTemplateValue = (val) =>
  val != null && (typeof val !== "string" || String(val).trim() !== "");

const applyRunTemplateOverrides = (defaults, templateData, activityTypes) => {
  if (!templateData) return defaults;

  const next = { ...defaults };
  if (hasTemplateValue(templateData.details)) {
    next.details = templateData.details;
  }
  if (templateData.units != null && templateData.units !== "") {
    next.units = templateData.units;
  }
  if (templateData.uncharged != null) {
    next.uncharged = Boolean(templateData.uncharged);
  }
  if (hasTemplateValue(templateData.activityCode) && activityTypes?.length) {
    const typeFromApi = activityTypes.find(
      (item) => item.code === templateData.activityCode,
    );
    if (typeFromApi) {
      next.activityType = typeFromApi;
    }
  }
  return next;
};

const CaseActivityForm = (props) => {
  const { uploadForm } = useUploadForm();
  const matterDetails = props.matterDetails;
  const isClosedMatter = Boolean(matterDetails?.closed);
  const restrictUnitsForClosedMatter =
    isClosedMatter && (!props.isView || props.isDuplicate);

  const isMultiMode = !props.isView && props.component !== "DOC-TEMPLATE";

  const [details, setDetails] = useState(props.defaultDetails || "");
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [rateInfoText, setRateInfoText] = useState(0);
  const [modalSuccess, setModalSuccess] = useState(undefined);
  const [newMaterId, setNewMatterId] = useState(undefined);
  const matterId = props.reference ?? props.viewActivity?.matterId;
  const { data: activityTypes } = useActiveActivityTypes();
  const { data: activityTagsFromHook, loading: activityTagsLoadingFromHook } =
    useMatterActivityTags(props.activityTags ? null : matterId);
  const activityTags = props.activityTags ?? activityTagsFromHook;
  const activityTagsLoading =
    props.activityTagsLoading ?? activityTagsLoadingFromHook;
  const [selectedTags, setSelectedTags] = useState([]);
  const [originalTags, setOriginalTags] = useState([]);
  const [viewFile, setViewFile] = useState("");
  const [pendingType, setPendingType] = useState(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isBilled, setIsBilled] = useState(false);
  const [uncharged, setUncharged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(null);
  const [inputState, setInputState] = useState({
    [CONSTANT.INPUT.DETAILS]: "",
    [CONSTANT.INPUT.UNITS]:
      (!props.isView || props.isDuplicate) && matterDetails?.closed
        ? 0
        : !props.isView
          ? 1
          : 0,
    [CONSTANT.INPUT.MPU]: !props.view ? 6 : 0,
    [CONSTANT.INPUT.RATE]: 0.0,
    [CONSTANT.INPUT.VALUE]: 0.0,
  });

  const [comboBoxState, setComboBoxState] = useState({
    [CONSTANT.COMBOBOX.TYPE]: "",
    [CONSTANT.COMBOBOX.FEE_EARNER]: "",
  });

  const [dateState, setDateState] = useState({
    [CONSTANT.DATE.DATE]: new Date(),
  });
  const originalEditDateRef = React.useRef(null);
  const isViewEmailDoc = React.useMemo(() => {
    const name = props.viewActivity?.documentName || "";
    const ext = name.split(".").pop()?.toLowerCase();
    return ext === "eml" || ext === "msg";
  }, [props.viewActivity?.documentName]);
  const { data: feeEarnerData, loading } = useActiveFeeEarner();
  const { data: allUsersData } = useUsers();
  const buildFeeEarnerValue = React.useCallback(
    (userRef) => {
      if (!userRef) return "";
      const active = feeEarnerData?.find((item) => item.userRef === userRef);
      if (active) {
        return { display: active.display, userRef: active.userRef };
      }
      const anyUser = allUsersData?.find((item) => item.userRef === userRef);
      if (anyUser) {
        const display =
          anyUser.display ||
          (anyUser.displayname
            ? `${anyUser.userRef} - ${anyUser.displayname}`
            : anyUser.userRef);
        return { display, userRef: anyUser.userRef };
      }
      return { display: userRef, userRef };
    },
    [feeEarnerData, allUsersData],
  );
  const [feeEarner, setFeeEarner] = useState([]);
  const [showPdf, setShowPdf] = useState(false);
  const [filesData, setFilesData] = useState([]);
  const [fileId, setFileId] = useState([]);

  // Multi-file entries (add mode only)
  const [fileEntries, setFileEntries] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [invalidKeys, setInvalidKeys] = useState([]);

  const isFormValid = (formState) => {
    const t = formState?.type;
    const d = formState?.date;
    const fe = formState?.feeEarner;
    const det = formState?.details;
    const typeOk =
      t && typeof t === "object" && t.activityTypeID != null && t.activityTypeID !== "";
    const dateOk = d != null && !Number.isNaN(new Date(d).getTime());
    const feOk = fe && fe.userRef;
    const detOk = det && String(det).trim() !== "";
    return typeOk && dateOk && feOk && detOk;
  };

  const isActivityTypeValid = () => {
    const t = comboBoxState[CONSTANT.COMBOBOX.TYPE];
    return (
      t &&
      typeof t === "object" &&
      t.activityTypeID != null &&
      t.activityTypeID !== ""
    );
  };

  const isActivityDateValid = () => {
    const d = dateState[CONSTANT.DATE.DATE];
    if (d == null) return false;
    return !Number.isNaN(new Date(d).getTime());
  };

  const filterFeeEarner = (filter) => {
    const data = feeEarnerData.slice();
    return filterBy(data, filter);
  };
  const feeEarnerfilterChange = (event) => {
    setFeeEarner(filterFeeEarner(event.filter));
  };

  const normalizedProps = React.useMemo(() => {
    if (props.isView && props.viewActivity) {
      const unitsForDuplicateClosed =
        props.isDuplicate && matterDetails?.closed
          ? 0
          : (props.viewActivity?.units ?? 0);
      return {
        feeEarnerRef: props.viewActivity.feeEarnerRef,
        document: {
          id: props.viewActivity.documentId,
          file: props.viewActivity.documentFile,
        },
        isBilled: props.viewActivity.isBilled || false,
        details: props.viewActivity.details,
        units: unitsForDuplicateClosed,
        mpu: props.viewActivity?.mpu || 6,
        rate: props.viewActivity?.rate || 0,
        value: props.viewActivity?.value || 0,
        uncharged: props.viewActivity?.uncharged === true,
        type: props.viewActivity.type,
        typeId: props.viewActivity.typeId,
        date: new Date(props.viewActivity.date),
        tags: props.viewActivity.tags || [],
        isDuplicate: props.isDuplicate || false,
        mode: "view",
      };
    }
    const templateData =
      props.component === "DOC-TEMPLATE"
        ? props.droppedFilesActivity?.[0]
        : null;

    return applyRunTemplateOverrides(
      {
        feeEarnerRef: props.feeEarnerRef,
        document: {
          files: props.droppedFilesActivity,
        },
        isBilled: false,
        details: props.defaultDetails || "",
        units: matterDetails?.closed ? 0 : 1,
        mpu: 6,
        rate: 0,
        value: 0,
        uncharged: false,
        activityType: null,
        tags: [],
        date: new Date(),
        isDuplicate: false,
        mode: "add",
      },
      templateData,
      activityTypes,
    );
  }, [
    props.isView,
    props.viewActivity,
    props.feeEarnerRef,
    props.droppedFilesActivity,
    props.defaultDetails,
    props.defaultType,
    props.component,
    matterDetails?.closed,
    props.isDuplicate,
    props.lockedDate,
    activityTypes,
  ]);

  const initializeForm = React.useCallback(() => {
    if (!feeEarnerData || loading) return;

    setFeeEarner(feeEarnerData);
    setIsBilled(normalizedProps.isBilled);
    setUncharged(normalizedProps.uncharged);
    setDetails(normalizedProps.details);
    setInputState({
      [CONSTANT.INPUT.UNITS]: normalizedProps.units,
      [CONSTANT.INPUT.MPU]: normalizedProps.mpu,
      [CONSTANT.INPUT.RATE]: normalizedProps.rate,
      [CONSTANT.INPUT.VALUE]: normalizedProps.value,
    });
    const feeEarnerValue = buildFeeEarnerValue(normalizedProps.feeEarnerRef);

    const typeObj =
      normalizedProps.activityType ||
      props.defaultType ||
      {
        name: normalizedProps.type || "",
        activityTypeID: normalizedProps.typeId ?? null,
      };

    setComboBoxState((prev) => ({
      ...prev,
      [CONSTANT.COMBOBOX.FEE_EARNER]: feeEarnerValue,
      [CONSTANT.COMBOBOX.TYPE]: typeObj,
    }));

    if (normalizedProps.mode === "add" && feeEarnerValue?.userRef) {
      getMatterRateValueFor(feeEarnerValue.userRef);
    }
    setDateState({
      [CONSTANT.DATE.DATE]: normalizedProps.date,
    });
    originalEditDateRef.current =
      normalizedProps.mode === "view" ? normalizedProps.date : null;

    if (normalizedProps.mode === "view" && normalizedProps.document.id) {
      loadDocument(normalizedProps.document);
    }
  }, [loading, normalizedProps, props.defaultType]);

  const tagsHydratedRef = React.useRef(false);
  useEffect(() => {
    if (tagsHydratedRef.current || activityTagsLoading) return;
    if (normalizedProps.mode !== "view") return;

    setOriginalTags(normalizedProps.tags || []);
    setSelectedTags(
      mapActivityTagsToSelection(normalizedProps.tags, activityTags),
    );
    tagsHydratedRef.current = true;
  }, [
    activityTags,
    activityTagsLoading,
    normalizedProps.mode,
    normalizedProps.tags,
  ]);

  const loadDocument = async (document) => {
    try {
      let fileData;

      if (normalizedProps.isDuplicate && document.file) {
        fileData = document.file;
      } else if (document.id) {
        const { data } = await getDocument(document.id);
        fileData = data?.data?.documentFile;
      } else if (document.file?.downloadURL) {
        fileData = document.file;
      }

      if (fileData) {
        setFilesData([fileData]);
        setFileId(fileData.fileID);

        if (!normalizedProps.isDuplicate && fileData.downloadURL) {
          const response = await fetch(fileData.downloadURL);
          const blob = await response.blob();
          const base64data = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          setViewFile(base64data.split(",")[1]);
        }
      }
    } catch (error) {
      console.error("Failed to load document:", error);
    }
  };

  useEffect(() => {
    if (feeEarnerData && !loading) {
      initializeForm();
    }
  }, [loading, feeEarnerData, initializeForm]);

  // Build a blank form template for new entries, seeded from current props/defaults.
  const buildBlankForm = React.useCallback(() => {
    const selectedFE = buildFeeEarnerValue(props.feeEarnerRef);
    const currentRate = Number(inputState[CONSTANT.INPUT.RATE]) || 0;
    const currentMpu = Number(inputState[CONSTANT.INPUT.MPU]) || 6;
    const initialUnits = matterDetails?.closed ? 0 : 1;
    const initialValue =
      currentRate && currentMpu && initialUnits
        ? (currentRate / 60) * (initialUnits * currentMpu)
        : 0;
    return {
      type: props.defaultType || "",
      date: new Date(),
      feeEarner: selectedFE,
      details: props.defaultDetails || "",
      units: initialUnits,
      mpu: currentMpu,
      rate: currentRate,
      value: initialValue,
      uncharged: false,
      tags: [],
    };
  }, [
    buildFeeEarnerValue,
    props.feeEarnerRef,
    props.defaultDetails,
    props.defaultType,
    matterDetails?.closed,
    inputState,
  ]);

  const inferFromFile = React.useCallback(
    (form, filename, subject) => {
      if (!activityTypes) return form;
      const ext = filename?.split(".").pop()?.toLowerCase();
      let next = { ...form };
      if (ext === "eml" || ext === "msg") {
        if (!props.defaultType) {
          const emailType = activityTypes.find((item) => item.code === "EO");
          if (emailType) next.type = emailType;
        } else {
          next.type = props.defaultType;
        }
        if (subject) next.details = subject;
      } else if (ext === "doc" || ext === "docx") {
        const docType = activityTypes.find((item) =>
          item.name?.toLowerCase()?.includes("document"),
        );
        if (docType) next.type = docType;
      }
      return next;
    },
    [activityTypes],
  );

  const startUploadFor = async (entryKey, file) => {
    try {
      const fileData = { filename: file.name, fileID: "" };
      const result = await uploadForm(fileData, file, {
        documentType: FileDocumentTypes.CaseFile,
        matterId: props.reference,
      });
      const uploaded = result?.data?.data || {};

      setFileEntries((prev) =>
        prev.map((en) => {
          if (en.key !== entryKey) return en;
          const inferredForm = inferFromFile(
            en.form,
            uploaded.filename,
            uploaded.subject,
          );
          return {
            ...en,
            filename: uploaded.filename || en.filename,
            fileData: uploaded,
            isUploading: false,
            form: inferredForm,
          };
        }),
      );

      // Mirror into active hook state if this entry is currently active
      setActiveKey((curActive) => {
        if (curActive === entryKey) {
          setFilesData([uploaded]);
          setFileId(uploaded.fileID || []);
          const ext = uploaded.filename?.split(".").pop()?.toLowerCase();
          if (ext === "eml" || ext === "msg") {
            const emailType =
              props.defaultType ||
              activityTypes?.find((it) => it.code === "EO");
            if (emailType) {
              setComboBoxState((prevCb) => ({
                ...prevCb,
                [CONSTANT.COMBOBOX.TYPE]: emailType,
              }));
            }
            if (uploaded.subject) setDetails(uploaded.subject);
          } else if (ext === "doc" || ext === "docx") {
            const docType = activityTypes?.find((it) =>
              it.name?.toLowerCase()?.includes("document"),
            );
            if (docType) {
              setComboBoxState((prevCb) => ({
                ...prevCb,
                [CONSTANT.COMBOBOX.TYPE]: docType,
              }));
            }
          }
        }
        return curActive;
      });
    } catch (err) {
      console.error("File upload failed:", err);
      setFileEntries((prev) =>
        prev.map((en) =>
          en.key === entryKey ? { ...en, isUploading: false, uploadError: true } : en,
        ),
      );
      toast.error(`Failed to upload ${file?.name || "file"}`);
    }
  };

  // Snapshot current hook state into the active entry's form.
  const snapshotActive = React.useCallback(() => {
    if (!activeKey) return null;
    const snap = {
      type: comboBoxState[CONSTANT.COMBOBOX.TYPE],
      date: dateState[CONSTANT.DATE.DATE],
      feeEarner: comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER],
      details: details,
      units: inputState[CONSTANT.INPUT.UNITS],
      mpu: inputState[CONSTANT.INPUT.MPU],
      rate: inputState[CONSTANT.INPUT.RATE],
      value: inputState[CONSTANT.INPUT.VALUE],
      uncharged: uncharged,
      tags: selectedTags,
    };
    return snap;
  }, [
    activeKey,
    comboBoxState,
    dateState,
    details,
    inputState,
    uncharged,
    selectedTags,
  ]);

  const applyFormToHooks = (form, fileData) => {
    const enrichedFE =
      form.feeEarner && typeof form.feeEarner === "object" && form.feeEarner.userRef
        ? buildFeeEarnerValue(form.feeEarner.userRef)
        : form.feeEarner ?? "";
    setComboBoxState({
      [CONSTANT.COMBOBOX.TYPE]: form.type ?? "",
      [CONSTANT.COMBOBOX.FEE_EARNER]: enrichedFE,
    });
    setDateState({ [CONSTANT.DATE.DATE]: form.date });
    setDetails(form.details ?? "");
    setInputState({
      [CONSTANT.INPUT.UNITS]: form.units ?? 0,
      [CONSTANT.INPUT.MPU]: form.mpu ?? 6,
      [CONSTANT.INPUT.RATE]: form.rate ?? 0,
      [CONSTANT.INPUT.VALUE]: form.value ?? 0,
    });
    setUncharged(Boolean(form.uncharged));
    setSelectedTags(form.tags || []);
    if (fileData) {
      setFilesData([fileData]);
      setFileId(fileData.fileID || []);
    } else {
      setFilesData([]);
      setFileId([]);
    }
  };

  const switchActive = (newKey) => {
    if (newKey === activeKey) return;
    const snap = snapshotActive();
    setFileEntries((prev) => {
      const next = prev.map((en) =>
        en.key === activeKey && snap ? { ...en, form: snap } : en,
      );
      const target = next.find((en) => en.key === newKey);
      if (target) {
        applyFormToHooks(target.form, target.fileData);
      }
      return next;
    });
    setActiveKey(newKey);
  };

  // Entry-pickup of dropped/picked files
  const addFilesAsEntries = (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);

    const newEntries = arr.map((f) => {
      const filename = f?.name || f?.filename || "file";
      // If it's already an uploaded file object (e.g., DOC-TEMPLATE prepared data), don't re-upload.
      const alreadyUploaded = !!(f?.fileID || f?.downloadURL);
      const rawDate = props.lockedDatesByFile?.[filename];
      const perFileDateObj = rawDate ? new Date(rawDate) : null;
      const perFileDate =
        perFileDateObj && !isNaN(perFileDateObj.getTime())
          ? perFileDateObj
          : null;
      const blank = buildBlankForm();
      return {
        key: nextEntryKey(),
        filename,
        fileData: alreadyUploaded ? f : null,
        isUploading: !alreadyUploaded,
        uploadError: false,
        rawFile: alreadyUploaded ? null : f,
        lockedDate: perFileDate,
        form: perFileDate ? { ...blank, date: perFileDate } : blank,
      };
    });

    setFileEntries((prev) => {
      // If first entry being added and there are no previous, set active to new[0]
      const merged = [...prev, ...newEntries];
      return merged;
    });

    // Kick off uploads (only for raw files)
    newEntries.forEach((en) => {
      if (en.rawFile) startUploadFor(en.key, en.rawFile);
    });

    // If nothing was active, activate the first newly added
    if (!activeKey && newEntries.length > 0) {
      const first = newEntries[0];
      setActiveKey(first.key);
      applyFormToHooks(first.form, first.fileData);
    }
  };

  // Snapshot of current hook state without requiring activeKey (used for initial seeding)
  const snapshotActiveCurrent = () => ({
    type: comboBoxState[CONSTANT.COMBOBOX.TYPE],
    date: dateState[CONSTANT.DATE.DATE],
    feeEarner: comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER],
    details: details,
    units: inputState[CONSTANT.INPUT.UNITS],
    mpu: inputState[CONSTANT.INPUT.MPU],
    rate: inputState[CONSTANT.INPUT.RATE],
    value: inputState[CONSTANT.INPUT.VALUE],
    uncharged: uncharged,
    tags: selectedTags,
  });

  const handledDroppedRef = React.useRef(null);
  useEffect(() => {
    if (!props.droppedFilesActivity?.length) return;
    if (normalizedProps.mode !== "add") return;
    if (!feeEarnerData || loading) return;
    if (handledDroppedRef.current === props.droppedFilesActivity) return;
    handledDroppedRef.current = props.droppedFilesActivity;

    if (props.component === "DOC-TEMPLATE") {
      handleDocTemplate(props.droppedFilesActivity[0]);
      return;
    }
    addFilesAsEntries(props.droppedFilesActivity);
  }, [props.droppedFilesActivity, loading, feeEarnerData]);

  const buildPayloadFromForm = (form, entryFileId, tagsOriginal = []) => {
    const tags = buildTagsPayload(form.tags, tagsOriginal);
    return {
      typeId: form.type?.activityTypeID || null,
      date: form.date ? formatDateForAPI(form.date) : null,
      details: form.details,
      feeEarnerRef: form.feeEarner?.userRef || null,
      units: (() => {
        const u = parseInt(form.units, 10);
        return Number.isNaN(u) ? null : u;
      })(),
      mpu: parseInt(form.mpu) || null,
      rate: parseFloat(form.rate) || null,
      value: parseFloat(form.value) || null,
      uncharged: Boolean(form.uncharged),
      fileId: entryFileId || null,
      subType: "None",
      ...(tags.length ? { tags } : {}),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // View / Update path — unchanged single-activity behaviour
    if (props.isView && !props?.isDuplicate) {
      const currentDate = dateState[CONSTANT.DATE.DATE];
      const originalDate = originalEditDateRef.current;
      const dateUnchanged =
        currentDate &&
        originalDate &&
        new Date(currentDate).getTime() === new Date(originalDate).getTime();
      const baseDataObject = {
        typeId: comboBoxState[CONSTANT.COMBOBOX.TYPE].activityTypeID || null,
        date:
          !currentDate || dateUnchanged
            ? null
            : formatDateForAPI(currentDate),
        details: details,
        feeEarnerRef: comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER].userRef || null,
        units: (() => {
          const u = parseInt(inputState[CONSTANT.INPUT.UNITS], 10);
          return Number.isNaN(u) ? null : u;
        })(),
        mpu: parseInt(inputState[CONSTANT.INPUT.MPU]) || null,
        rate: parseFloat(inputState[CONSTANT.INPUT.RATE]) || null,
        value: parseFloat(inputState[CONSTANT.INPUT.VALUE]) || null,
        uncharged: Boolean(uncharged),
        fileId: fileId.length ? fileId : null,
        subType: "None",
      };
      if (
        baseDataObject.details === "" ||
        baseDataObject.feeEarnerRef === null ||
        !isActivityTypeValid() ||
        !isActivityDateValid()
      ) {
        setError(true);
        return;
      }

      setIsSaving(true);
      try {
        const resolvedTags = await resolveSelectedTagsForSave(selectedTags);
        const tagsPayload = buildTagsPayload(resolvedTags, originalTags);
        const dataObject = {
          ...baseDataObject,
          ...(tagsPayload.length ? { tags: tagsPayload } : {}),
        };
        const res = await updateMatterActivity(
          props.viewActivity.matterId,
          props.viewActivity.activityId,
          dataObject,
        );
        if (res?.status === 200) {
          toast.success("Activity Updated Sucessfully");
          props.getActivitiesData();
          props.setshowMatterForm(false);
        }
      } catch (err) {
      } finally {
        setIsSaving(false);
      }
      setError(false);
      return;
    }

    // Add path: handle both single-form (no entries yet) and multi-entry cases.
    const snap = snapshotActiveCurrent();
    let entriesToSave;
    if (fileEntries.length === 0) {
      const pseudoEntry = {
        key: "single-no-file",
        filename: filesData[0]?.filename || props.viewActivity?.documentName || "",
        fileData: filesData[0] || null,
        form: snap,
      };
      entriesToSave = [pseudoEntry];
    } else {
      entriesToSave = fileEntries.map((en) =>
        en.key === activeKey ? { ...en, form: snap } : en,
      );
    }

    // Validate every entry
    const invalid = entriesToSave.filter((en) => !isFormValid(en.form));
    if (invalid.length > 0) {
      setInvalidKeys(invalid.map((en) => en.key));
      setError(true);
      if (fileEntries.length > 0) {
        // Persist snapshot to the entries list
        setFileEntries(entriesToSave);
        // Switch to first invalid if not already active
        if (invalid[0].key !== activeKey) {
          const target = invalid[0];
          applyFormToHooks(target.form, target.fileData);
          setActiveKey(target.key);
        }
      }
      return;
    }
    setInvalidKeys([]);
    setError(false);

    setIsSaving(true);
    const total = entriesToSave.length;
    const succeededKeys = [];
    const failedKeys = [];
    let lastResMatterId;

    for (let i = 0; i < entriesToSave.length; i++) {
      const entry = entriesToSave[i];
      setSaveProgress({ current: i + 1, total });
      const entryFileId =
        entry.fileData?.fileID ||
        entry.fileData?.fileId ||
        (props.isDuplicate ? props.viewActivity?.fileId : null);
      try {
        const resolvedTags = await resolveSelectedTagsForSave(entry.form.tags);
        const payload = buildPayloadFromForm(
          { ...entry.form, tags: resolvedTags },
          entryFileId,
        );
        const res = await saveActivity(props.reference, payload);
        if (res?.status === 200) {
          succeededKeys.push(entry.key);
          lastResMatterId = res.data?.data?.matterId;
        } else {
          failedKeys.push(entry.key);
        }
      } catch (err) {
        if (err?.message?.includes("Failed to create tag")) {
          toast.error(err.message);
        }
        failedKeys.push(entry.key);
      }
    }

    setIsSaving(false);
    setSaveProgress(null);
    if (lastResMatterId !== undefined) setNewMatterId(lastResMatterId);

    if (failedKeys.length === 0) {
      if (total > 1) {
        toast.success(`${total} activities added successfully`);
      } else {
        toast.success("Activity Added Sucessfully");
      }
      props.getActivitiesData();
      props.setshowMatterForm(false);
      return;
    }

    // Partial failure: refresh, drop successful entries, keep failed.
    if (succeededKeys.length > 0) {
      toast.success(
        `${succeededKeys.length} of ${total} activities added successfully`,
      );
      props.getActivitiesData();
    }
    failedKeys.forEach((k) => {
      const e = entriesToSave.find((x) => x.key === k);
      toast.error(`Failed to save activity for ${e?.filename || "file"}`);
    });

    if (fileEntries.length > 0) {
      const remaining = entriesToSave.filter((en) =>
        failedKeys.includes(en.key),
      );
      setFileEntries(remaining);
      if (remaining.length === 0) {
        props.setshowMatterForm(false);
      } else if (!remaining.some((en) => en.key === activeKey)) {
        // Active was saved — switch to first remaining
        const target = remaining[0];
        applyFormToHooks(target.form, target.fileData);
        setActiveKey(target.key);
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const updateModalState = (modalShow, message, success) => {
    setShowModal(modalShow);
    setModalMessage(message);
    setModalSuccess(success);
  };

  const handleInputChange = (value, label) => {
    setInputState({
      ...inputState,
      [label]: value,
    });
  };

  const handleComboBoxChange = (e, label) => {
    setComboBoxState({
      ...comboBoxState,
      [label]: e.value,
    });
    if (
      label === CONSTANT.COMBOBOX.FEE_EARNER &&
      !props.isView &&
      e.value?.userRef
    ) {
      getMatterRateValueFor(e.value.userRef);
    }
  };

  const handleDrop = (event) => {
    if (props.isView) return;
    if (props.component === "DOC-TEMPLATE") return;
    if (!event || event.length === 0) return;
    addFilesAsEntries(event);
  };

  const handleDateChange = (e, label) => {
    setDateState({
      ...dateState,
      [label]: e.value,
    });
  };

  const handleTypeChange = (value) => {
    setComboBoxState({
      ...comboBoxState,
      [CONSTANT.COMBOBOX.TYPE]: value.value,
    });
  };
  const filePickerRef = React.useRef();
  const uploadRef = React.createRef();

  // Picker upload — supports multi-select; in add mode pipes to addFilesAsEntries
  const handleUpload = async (e) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) return;

    if (isMultiMode) {
      addFilesAsEntries(files);
      // Reset picker so user can pick the same file again later
      if (e.target) e.target.value = null;
      return;
    }

    // Legacy single-file path (View/Update or DOC-TEMPLATE — DOC-TEMPLATE uses handleDocTemplate)
    try {
      setIsFileLoading(true);
      const file = files[0];
      const fileData = { filename: file.name, fileID: "" };
      const dataChoosen = await uploadForm(fileData, file, {
        documentType: FileDocumentTypes.CaseFile,
        matterId: props.reference,
      });

      const uploaded = dataChoosen?.data?.data || {};
      setFileId(uploaded.fileID || []);
      setFilesData([{ ...uploaded }]);

      const fileExt = uploaded.filename?.split(".").pop()?.toLowerCase();
      if (fileExt === "eml" || fileExt === "msg") {
        const emailType =
          props.defaultType ||
          activityTypes.find((item) => item.code === "EO");
        if (emailType) {
          setComboBoxState((prev) => ({
            ...prev,
            [CONSTANT.COMBOBOX.TYPE]: emailType,
          }));
        }
        if (uploaded.subject) setDetails(uploaded.subject);
      } else if (fileExt === "doc" || fileExt === "docx") {
        const docType = activityTypes.find((item) =>
          item.name?.toLowerCase().includes("document"),
        );
        if (docType) {
          setComboBoxState((prev) => ({
            ...prev,
            [CONSTANT.COMBOBOX.TYPE]: docType,
          }));
        }
      }
    } catch (error) {
      console.error("File upload failed:", error);
    } finally {
      setIsFileLoading(false);
      if (e?.target) e.target.value = null;
    }
  };

  const handleDocTemplate = async (data) => {
    try {
      setIsFileLoading(true);
      setFileId(data.fileID);
      setFilesData([{ ...data }]);

      const hasApiActivityCode = hasTemplateValue(data?.activityCode);
      if (!hasApiActivityCode && !props.defaultType) {
        switch (data?.filename?.split(".").pop()) {
          case "eml":
          case "msg":
            setComboBoxState((prev) => ({
              ...prev,
              [CONSTANT.COMBOBOX.TYPE]:
                activityTypes.filter((item) => item.code === "EO")[0] || null,
            }));
            break;
          case "doc":
          case "docx":
            setComboBoxState((prev) => ({
              ...prev,
              [CONSTANT.COMBOBOX.TYPE]:
                activityTypes.filter((item) =>
                  item.name?.toLowerCase()?.includes("document"),
                )[0] || null,
            }));
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.error("Error handling document template:", error);
    } finally {
      setIsFileLoading(false);
    }
  };

  // Remove an entry by key (multi-mode). Closes modal if removing the last one.
  const removeEntry = (entryKey) => {
    const next = fileEntries.filter((en) => en.key !== entryKey);
    setFileEntries(next);
    setInvalidKeys((cur) => cur.filter((k) => k !== entryKey));

    if (next.length === 0) {
      setActiveKey(null);
      setFilesData([]);
      setFileId([]);
      props.setshowMatterForm();
      return;
    }
    if (entryKey === activeKey) {
      const target = next[0];
      applyFormToHooks(target.form, target.fileData);
      setActiveKey(target.key);
    }
  };

  // Legacy onFileRemove kept for compatibility with view-mode big-X button
  const onFileRemove = (fid) => {
    setFilesData((prev) => prev.filter((file) => file.fileID !== fid));
  };

  const handleNewDocument = async () => {
    try {
      setIsFileLoading(true);
      const data = {
        clientId: props.matterDetails?.clientId || 0,
        matterId: props.reference,
        userRef: props.feeEarnerRef || "",
        supplierRef: "",
        nominalRef: "",
        documentType: 1,
      };
      const response = await getNewBlankDocument({ data });
      const fileData = response.data.data;

      setFileId(fileData.fileID);
      setFilesData([{ ...fileData }]);

      const documentActivityType = activityTypes.filter((item) =>
        item.name?.toLowerCase()?.includes("document"),
      )[0];

      if (documentActivityType) {
        setComboBoxState({
          ...comboBoxState,
          [CONSTANT.COMBOBOX.TYPE]: documentActivityType,
        });
      }

      toast.success("New document created successfully");
    } catch (error) {
      console.error("Error creating new document:", error);
    } finally {
      setIsFileLoading(false);
    }
  };

  const getMatterRateValueFor = async (userRef) => {
    try {
      const data = await getMatterRates(props.reference, userRef);
      const fetchedRate = data?.data?.data?.rateValue;
      setInputState((prev) => ({
        ...prev,
        [CONSTANT.INPUT.RATE]: fetchedRate,
      }));
      setRateInfoText(data?.data?.data?.rateInfo);
      if (fetchedRate != null) {
        setFileEntries((prev) =>
          prev.map((en) => {
            const units = Number(en.form?.units) || 0;
            const mpu = Number(en.form?.mpu) || 0;
            const value =
              en.form?.uncharged || !units || !mpu
                ? 0
                : (fetchedRate / 60) * (units * mpu);
            return {
              ...en,
              form: { ...en.form, rate: fetchedRate, value },
            };
          }),
        );
      }
    } catch (error) { }
  };

  React.useEffect(() => {
    setInputState((prevState) => {
      if (uncharged) {
        return {
          ...prevState,
          [CONSTANT.INPUT.VALUE]: 0,
        };
      }
      if (
        prevState[CONSTANT.INPUT.RATE] &&
        prevState[CONSTANT.INPUT.UNITS] != null &&
        prevState[CONSTANT.INPUT.MPU]
      ) {
        const value =
          (prevState[CONSTANT.INPUT.RATE] / 60) *
          (prevState[CONSTANT.INPUT.UNITS] * prevState[CONSTANT.INPUT.MPU]);
        return {
          ...prevState,
          [CONSTANT.INPUT.VALUE]: value,
        };
      }
      return prevState;
    });
  }, [
    uncharged,
    inputState[CONSTANT.INPUT.RATE],
    inputState[CONSTANT.INPUT.UNITS],
    inputState[CONSTANT.INPUT.MPU],
  ]);

  const showFileList = isMultiMode && fileEntries.length > 1;
  const activeEntry = activeKey
    ? fileEntries.find((en) => en.key === activeKey)
    : null;
  const activeIsUploading = !!activeEntry?.isUploading;

  const formInputWidth = showFileList ? "100%" : "260px";
  const formPaddingClass = showFileList ? "px-3" : "px-5";
  const timeInputWidth = showFileList ? "100%" : "286px";

  const tagOptions = React.useMemo(() => {
    const active = (activityTags || []).map(normalizeTag);
    const selectedInactive = (selectedTags || [])
      .map(normalizeTag)
      .filter(
        (tag) =>
          getTagId(tag) != null &&
          !active.some((item) => getTagId(item) === getTagId(tag)),
      );
    return [...active, ...selectedInactive];
  }, [activityTags, selectedTags]);

  const [filteredTagOptions, setFilteredTagOptions] = useState([]);
  const [tagFilterText, setTagFilterText] = useState("");

  React.useEffect(() => {
    setFilteredTagOptions(tagOptions);
  }, [tagOptions]);

  const allowNewTag = React.useMemo(() => {
    const text = tagFilterText.trim();
    if (!text) return false;
    return !tagOptions.some(
      (tag) => tag.name?.trim().toLowerCase() === text.toLowerCase(),
    );
  }, [tagFilterText, tagOptions]);

  const onTagOptionsFilterChange = (event) => {
    setTagFilterText(event.filter?.value ?? "");
    setFilteredTagOptions(filterBy(tagOptions, event.filter));
  };

  const normalizedSelectedTags = React.useMemo(
    () => (selectedTags || []).map(normalizeTag),
    [selectedTags],
  );

  const resolveTagFromSelection = React.useCallback(
    (item) => {
      if (typeof item === "string") {
        const name = item.trim();
        if (!name) return null;
        const existing = findExistingTagByName(tagOptions, name, matterId);
        return existing ? normalizeTag(existing) : createPendingTag(name);
      }

      const tag = normalizeTag(item);
      const name = tag?.name?.trim();
      if (!name) return null;

      const id = getTagId(tag);
      const isPending =
        tag.isNew || String(id || "").startsWith(NEW_TAG_PREFIX);
      if (isPending) return tag;

      if (id != null) return tag;

      const existing = findExistingTagByName(tagOptions, name, matterId);
      return existing ? normalizeTag(existing) : createPendingTag(name);
    },
    [matterId, tagOptions],
  );

  const handleTagsChange = (e) => {
    const next = [];
    (e.value || []).forEach((item) => {
      const tag = resolveTagFromSelection(item);
      if (!tag) return;

      const duplicate = next.some(
        (existing) =>
          getTagId(existing) === getTagId(tag) ||
          existing.name?.trim().toLowerCase() ===
          tag.name?.trim().toLowerCase(),
      );
      if (!duplicate) next.push(tag);
    });
    setSelectedTags(next);
  };

  const tagChipRender = (tagData, li) => {
    const tag = tagData?.data?.[0];
    if (!isNewTag(tag)) return li;
    return React.cloneElement(
      li,
      li.props,
      <span className="d-inline-flex align-items-center gap-2">
        <span>{tag.name}</span>
        <PrimaryBadgeComponent
          text="New"
          className={`mb-0 py-0 px-2 text-uppercase fw-semibold ${activityStyles.newTagBadge}`}
        />
      </span>,
    );
  };

  const resolveSelectedTagsForSave = async (tags) => {
    if (!tags?.length) return [];
    const resolved = [];
    const createdByName = new Map();

    for (const tag of tags.map(normalizeTag)) {
      if (!isNewTag(tag)) {
        resolved.push(tag);
        continue;
      }

      const key = tag.name.trim().toLowerCase();
      if (createdByName.has(key)) {
        resolved.push(createdByName.get(key));
        continue;
      }

      const existing = findExistingTagByName(activityTags, tag.name, matterId);
      if (existing) {
        const normalized = normalizeTag(existing);
        createdByName.set(key, normalized);
        resolved.push(normalized);
        continue;
      }

      const response = await addActivityTagAPI({
        name: tag.name.trim(),
        active: true,
        colour: "none",
        matterId,
      });
      const createdTag = response?.data?.data;
      if (!createdTag) {
        throw new Error(`Failed to create tag "${tag.name}"`);
      }
      props.onTagCreated?.();
      const normalized = normalizeTag(createdTag);
      createdByName.set(key, normalized);
      resolved.push(normalized);
    }

    return resolved;
  };

  return (
    <div className={styles["matter-form-dialog"]}>
      <Dialog
        title={
          props.isView && !props?.isDuplicate ? "Update Activity" : "Add Activities"
        }
        onClose={props.setshowMatterForm}
        className={showFileList ? "dialog-xxl-tall" : "dialog-lg"}>
        <div className="d-flex flex-column w-100 h-100">
          <form
            id="matterForm"
            onSubmit={handleSubmit}
            className="flex-grow-1 h-100">
            <div className="row g-0 h-100">
              {showFileList && (
                <div
                  className={`${styles["activity-files-column"]} col-12 col-lg-3 d-flex flex-column py-4 px-3`}>
                  <div className={styles["activity-files-list"]}>
                    {fileEntries.map((en) => {
                      const isActive = en.key === activeKey;
                      const isInvalid = invalidKeys.includes(en.key);
                      return (
                        <div
                          key={en.key}
                          className={`${styles["activity-file-card"]} ${isActive ? styles["activity-file-card-active"] : ""} ${isInvalid ? styles["activity-file-card-invalid"] : ""}`}
                          onClick={() => switchActive(en.key)}>
                          <div className={styles["activity-file-card-icon"]}>
                            {fileIcon(en.filename)}
                          </div>
                          <div
                            className={styles["activity-file-card-name"]}
                            title={en.filename}>
                            {en.filename}
                          </div>
                          {en.isUploading ? (
                            <div
                              className="spinner-border spinner-border-sm"
                              style={{ width: 14, height: 14 }}
                              role="status"
                            />
                          ) : (
                            <div
                              className={`${styles["activity-file-card-remove"]} cursor-pointer`}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                removeEntry(en.key);
                              }}>
                              <RxCross2 size={16} color="red" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div
                className={`bgForm py-4 ${formPaddingClass} col-12 ${showFileList ? "col-lg-3 align-items-center" : "col-lg-4"} d-flex flex-column`}>
                <div className="">
                  <div className="">
                    <span className={`${styles["add-activity-form-text"]}`}>
                      {props.addFormText}
                    </span>
                  </div>
                  <FormComboBox
                    isStyled={true}
                    className="mt-4"
                    width={formInputWidth}
                    wf={true}
                    name={CONSTANT.COMBOBOX.TYPE}
                    styles={{
                      border:
                        error === true && !isActivityTypeValid() && "1px solid red",
                    }}
                    textField={"name"}
                    state={comboBoxState}
                    data={activityTypes}
                    val={comboBoxState[CONSTANT.COMBOBOX.TYPE]}
                    onChangeComboBox={handleTypeChange}
                  />

                  <FormDate
                    isStyled={true}
                    width={formInputWidth}
                    className={`${showFileList ? "" : "note-date-picker"} mt-3`}
                    label={CONSTANT.DATE.DATE}
                    onChangeDate={handleDateChange}
                    state={dateState}
                    disabled={Boolean(
                      (fileEntries.length > 0
                        ? activeEntry?.lockedDate
                        : props.lockedDate) ||
                      (props.isView && isViewEmailDoc),
                    )}
                    error={error === true && !isActivityDateValid()}
                  />

                  <FormComboBox
                    loading={loading}
                    isStyled={true}
                    className="mt-4"
                    width={formInputWidth}
                    wf={true}
                    styles={{
                      border:
                        error === true &&
                        !comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER] &&
                        "1px solid red",
                    }}
                    onChangeComboBox={handleComboBoxChange}
                    onFilterChange={feeEarnerfilterChange}
                    data={feeEarner}
                    textField="display"
                    val={comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER]}
                    state={comboBoxState}
                    name={CONSTANT.COMBOBOX.FEE_EARNER}
                  />
                  <div className="col-12 d-flex flex-column mt-4">
                    <Label className="label">Tags</Label>
                    <MultiSelect
                      className={`ps-1 ${activityStyles.activityTagsMultiselect}`}
                      data={filteredTagOptions}
                      value={normalizedSelectedTags}
                      onChange={handleTagsChange}
                      onFilterChange={onTagOptionsFilterChange}
                      dataItemKey="tagId"
                      textField="name"
                      placeholder="Select Tags"
                      loading={activityTagsLoading}
                      style={{ width: formInputWidth }}
                      filterable={true}
                      allowCustom={allowNewTag}
                      tagRender={tagChipRender}
                    />
                  </div>
                  <div className="col-12 d-flex flex-column mt-4">
                    <Label className={`label`}>Details</Label>
                    <TextArea
                      placeholder="Enter Details"
                      autoSize={true}
                      value={details}
                      style={{
                        height: "92px",
                        width: formInputWidth,
                        border: error === true && !details && "1px solid red",
                      }}
                      onChange={(e) => setDetails(e.value)}
                    />
                  </div>

                </div>
              </div>
              <div
                className={`col-12 ${showFileList ? "col-lg-6" : "col-lg-8"} d-flex flex-column`}>
                <Tile
                  heading={
                    <span className="d-flex align-items-center flex-wrap gap-2">
                      <span>
                        Time Record{isBilled ? " (Billed)" : ""}
                      </span>
                      {restrictUnitsForClosedMatter && (
                        <span className={styles.closedMatterText}>
                          [Closed Matter]
                        </span>
                      )}
                    </span>
                  }
                  className="m-3 p-3 ps-4 pe-4 pt-2 pb-0 d-flex flex-column flex-grow-0 flex-shrink-0"
                  height={280}>
                  <div className="ps-2 pb-4 flex-shrink-0">
                    <div className="mt-2 d-flex align-items-end gap-4 flex-nowrap col-12">
                      <div
                        className="d-flex flex-column right-align"
                        style={
                          showFileList
                            ? { flex: "1 1 auto", minWidth: 0 }
                            : { flex: "0 0 auto" }
                        }
                      >
                        <Label className="mb-1 label">Units</Label>
                        <NumericTextBox
                          style={{ width: timeInputWidth, height: "32px" }}
                          value={inputState[CONSTANT.INPUT.UNITS]}
                          onChange={(e) =>
                            handleInputChange(e.value, CONSTANT.INPUT.UNITS)
                          }
                          spinners={true}
                          format=""
                          disabled={isBilled || restrictUnitsForClosedMatter}
                        />
                      </div>
                      <div className="pb-2 ms-2 flex-shrink-0">
                        <Checkbox
                          label="Uncharged"
                          labelClassName="fw-semibold"
                          value={uncharged}
                          onChange={(e) => setUncharged(e.value)}
                          disabled={isBilled}
                        />
                      </div>
                    </div>

                    <div className="mt-2 d-flex align-items-end gap-4 flex-nowrap col-12">
                      <div
                        className="d-flex flex-column right-align"
                        style={
                          showFileList
                            ? { flex: "1 1 auto", minWidth: 0 }
                            : { flex: "0 0 auto" }
                        }
                      >
                        <div className="d-flex align-items-baseline ">
                          <Label className="label mb-1 ">Rate</Label>
                          <Tooltip anchorElement="target" position="top">
                            <img
                              className="ms-1 cursor-pointer"
                              src={infoIcon}
                              title={rateInfoText}
                              style={{
                                height: "14px",
                                width: "14px",
                              }}
                              alt="info"
                            />
                          </Tooltip>
                        </div>
                        <NumericTextBox
                          style={{ width: timeInputWidth, height: "32px" }}
                          value={inputState[CONSTANT.INPUT.RATE]}
                          onChange={(e) =>
                            handleInputChange(e.value, CONSTANT.INPUT.RATE)
                          }
                          spinners={false}
                          format="n2"
                          placeholder="0.00"
                          disabled={isBilled}
                        />
                      </div>
                      <div
                        aria-hidden="true"
                        className="pb-2 ms-2 flex-shrink-0"
                        style={{ visibility: "hidden" }}
                      >
                        <Checkbox label="Uncharged" labelClassName="fw-semibold" />
                      </div>
                    </div>
                    <div className="mt-2 d-flex align-items-end gap-4 flex-nowrap col-12">
                      <div
                        className="d-flex flex-column right-align"
                        style={
                          showFileList
                            ? { flex: "1 1 auto", minWidth: 0 }
                            : { flex: "0 0 auto" }
                        }
                      >
                        <div className="d-flex align-items-baseline ">
                          <Label className="label mb-1 ">Value</Label>
                          <Tooltip anchorElement="target" position="top">
                            <img
                              className="ms-1 cursor-pointer"
                              src={infoIcon}
                              title={`MPU = ${inputState[CONSTANT.INPUT.MPU]}`}
                              style={{
                                height: "14px",
                                width: "14px",
                              }}
                              alt="info"
                            />
                          </Tooltip>
                        </div>
                        <NumericTextBox
                          style={{ width: timeInputWidth, height: "32px" }}
                          value={inputState[CONSTANT.INPUT.VALUE]}
                          onChange={(e) =>
                            handleInputChange(e.value, CONSTANT.INPUT.VALUE)
                          }
                          spinners={false}
                          format="n2"
                          placeholder="0.00"
                          disabled={isBilled || uncharged}
                        />
                      </div>
                      <div
                        aria-hidden="true"
                        className="pb-2 ms-2 flex-shrink-0"
                        style={{ visibility: "hidden" }}
                      >
                        <Checkbox label="Uncharged" labelClassName="fw-semibold" />
                      </div>
                    </div>
                  </div>
                </Tile>
                <FileDragDrop onFilesDrop={handleDrop} uploadRef={uploadRef}>
                  <Tile
                    heading="Document"
                    className="p-3 mx-3 ps-4 pe-4 mb-3 d-flex flex-column flex-grow-1"
                    height={410}
                    headingItems={
                      props.isAddActivityBtn && (
                        <div className="d-flex gap-1 justify-content-center align-items-center">
                          {filesData.length >= 1 && (
                            <div
                              className={` rounded cursor-pointer ${styles["cross-icon"]}`}
                              onClick={() => {
                                if (props.isView) {
                                  toast.error("Attached document cant be changed");
                                  return;
                                }
                                if (isMultiMode && activeKey) {
                                  removeEntry(activeKey);
                                } else if (filesData.length) {
                                  setFilesData([]);
                                  setFileId([]);
                                }
                              }}>
                              <RxCross2 size={20} color="red" />
                            </div>
                          )}
                        </div>
                      )
                    }>
                    {filesData[0] && !activeIsUploading ? (
                      <div className="mt-2 flex-grow-1 overflow-auto">
                        <DocumentViewer
                          data={filesData}
                          onScreenComponent={true}
                          actionBar={false}
                          setDetails={setDetails}
                        />
                      </div>
                    ) : (
                      <div
                        onClick={(e) => filePickerRef?.current?.click()}
                        className={`d-flex align-items-center cursor-pointer justify-content-center flex-grow-1`}>
                        {isFileLoading || activeIsUploading ? (
                          <div>
                            <div
                              className="spinner-border spinner"
                              role="status"
                            ></div>
                          </div>
                        ) : (
                          <>
                            <div className="mt-3">
                              <p className={styles["doc-upload-text"]}>
                                {filesData[0]?.downloadURL ? "Update" : "Attach"}
                                <br /> Documents
                              </p>
                            </div>
                            <div className={styles["vertical-rule"]}></div>
                            <div
                              className={`${styles["file-upload-icon"]} d-flex align-items-center justify-content-center position-relative`}>
                              <RiUpload2Fill color="#DCDAFF" size={20} />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </Tile>
                </FileDragDrop>
              </div>
            </div>
          </form>
          <input
            name="file"
            type="file"
            multiple={isMultiMode}
            onChange={handleUpload}
            ref={filePickerRef}
            style={{
              visibility: "hidden",
            }}
          />
        </div>
        <DialogActionsBar>
          <div className="dialog-footer d-flex flex-wrap justify-content-end gap-2">
            <Button
              className="common-btn bgBtnSecondary border-0 me-2"
              onClick={props.setshowMatterForm}>
              Cancel
            </Button>
            <Button
              className="common-btn me-4"
              type="submit"
              form="matterForm"
              disabled={isSaving}>
              {isSaving
                ? saveProgress && saveProgress.total > 1
                  ? `Saving ${saveProgress.current} of ${saveProgress.total}…`
                  : "Saving..."
                : props.isView && !props?.isDuplicate
                  ? "Update"
                  : "Save"}
            </Button>
          </div>
        </DialogActionsBar>

        {showPdf && (
          <Window
            className="responsive-window"
            style={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              zIndex: 999999,
            }}
            title="View"
            onClose={() => setShowPdf(false)}
            initialWidth={"90%"}
            initialHeight={"80vh"}>
            <PDFViewer
              data={viewFile}
              onClose={(e) => {
                setViewFile("");
              }}
              style={{
                height: "100%",
                width: "100%",
              }}
            />
          </Window>
        )}
      </Dialog>

      <MatterModal
        showModal={showModal}
        message={modalMessage}
        success={modalSuccess}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default CaseActivityForm;
